import React from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { getOrgInfo, updateOrgInfo } from '@/services/orgs';
import type CurrentOrg from '@/types/api/CurrentOrg';
import type OrgGroup from '@/types/api/OrgGroup';
import { camelCaseKeys } from '@/utils/transformKeys';
import './_typedef';

type OrgResponse = {
  org: CurrentOrg;
};

const GroupTypes = {
  MEMBER: 'member',
  PERMISSION: 'permission',
};

const mapResponse = (data: OrgResponse) => {
  const orgData: CurrentOrg = camelCaseKeys(data?.org);
  const primaryAddress = (orgData?.addresses || [])?.find(
    ({ primary }) => primary,
  );
  const groups = (orgData?.groups || []).reduce(
    (res: OrgGroup[], { groupType, groupDisplayName, groupId }) => {
      if (groupType === GroupTypes.PERMISSION) {
        res.push({
          groupDisplayName,
          groupId,
        });
      }

      return res;
    },
    [],
  );

  return camelCaseKeys({
    ...orgData,
    groups,
    primaryAddress,
  });
};

/**
 * Uses the SWR library to fetch org data by ID.
 * @param {string?} orgId - ID of the org to be fetched.
 * @param {Object?} swrOptions - generally optional. See: https://swr.vercel.app/docs/options
 * @returns {SwrOrg}
 */
const useOrg = (
  orgId: string,
  swrOptions?: SWRConfiguration,
): {
  data: CurrentOrg;
  error: boolean;
  isBusy: boolean;
  isValidating: boolean;
  mutate: (updates: CurrentOrg) => Promise<void>;
} => {
  const [isBusy, setIsBusy] = React.useState(false);
  const { data, error, isValidating, mutate } = useSWR(
    orgId,
    async (id) => {
      const res = await getOrgInfo(id);
      return mapResponse(res.data);
    },
    swrOptions,
  );

  const mutateOrg = React.useCallback(
    async (updates: CurrentOrg) => {
      if (isBusy) return;
      setIsBusy(true);
      mutate((currentData: CurrentOrg) => ({ ...currentData, ...updates }), {
        revalidate: false,
      });
      const res = await updateOrgInfo(orgId, updates);
      mutate(mapResponse(res.data), { revalidate: false });
      setIsBusy(false);
    },
    [isBusy, orgId, mutate],
  );

  return {
    data,
    error,
    isBusy,
    isValidating,
    mutate: mutateOrg,
  };
};

export default useOrg;
